<template>
  <app-modal
    ref="modal"
    v-bind="$attrs"
    class="modal-container"
    content-class="modal-medium"
    title="インターネットアプライアンス ゾーン接続追加"
  >
    <template v-if="isLoaded" v-slot:body="{ cancel }">
      <validation-observer v-slot="{ handleSubmit }" as="div">
        <v-form novalidate @submit.stop.prevent="handleSubmit(submit)">
          <!-- BODY -->
          <v-card-text class="pa-0">
            <div class="modal-body">
              <v-container>
                <v-row>
                  <v-col>
                    <validation-provider
                      name="srcZone"
                      v-model="form.srcZone"
                      label="送信元ゾーン"
                      :rules="{ required: true }"
                      v-slot="{ componentField, errorMessage, meta }"
                    >
                      <app-badge
                        item="送信元ゾーン"
                        :isInfo="false"
                        :isRequired="true"
                      />
                      <app-select
                        :items="zoneOptions"
                        item-title="text"
                        item-value="value"
                        v-bind="componentField"
                        :error-messages="errorMessage"
                        :field-meta="meta"
                      />
                    </validation-provider>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <validation-provider
                      name="dstZone"
                      v-model="form.dstZone"
                      label="宛先ゾーン"
                      :rules="{
                        required: true,
                        duplicate_zone_relation: {
                          srcZone: form.srcZone,
                          relation: relations.zoneRelationList,
                        },
                      }"
                      v-slot="{ componentField, errorMessage, meta }"
                    >
                      <app-badge
                        item="宛先ゾーン"
                        :isInfo="false"
                        :isRequired="true"
                      />
                      <app-select
                        :items="dstZoneOptions"
                        item-title="text"
                        item-value="value"
                        v-bind="componentField"
                        :error-messages="errorMessage"
                        :field-meta="meta"
                      />
                    </validation-provider>
                  </v-col>
                </v-row>
              </v-container>
            </div>
          </v-card-text>
          <v-divider></v-divider>
          <!-- FOOTER -->
          <v-card-actions>
            <v-list-item class="w-100 pa-1">
              <template v-slot:append>
                <v-btn type="submit" variant="flat" color="primary">
                  追加
                </v-btn>
                <v-btn variant="outlined" color="primary" @click="cancel">
                  キャンセル
                </v-btn>
              </template>
            </v-list-item>
          </v-card-actions>
        </v-form>
      </validation-observer>
    </template>
  </app-modal>
</template>

<script lang="ts">
import type { PropType } from "vue";
import { defineComponent } from "vue";
import type { ZoneRelation } from "@/apis/ZoneRelationApi";
import type { Type1Zone } from "@/apis/Type1SiteApi";
import { VueFinalModal } from "vue-final-modal";
/** ゾーン接続追加用 */
export type Type1ZoneOption = Type1Zone & {
  type: "INTERNET" | "EXTRA" | "PRIVATE";
};

export default defineComponent({
  name: "InternetType1ZoneRelationRegister",
  props: {
    /** ゾーン関係設定排他情報 */
    zoneRelationKeyId: {
      type: String as PropType<string>,
      required: true,
    },
    relations: {
      type: Object as PropType<ZoneRelation>,
      required: true,
    },
  },
  data() {
    return {
      /** 初期通信完了フラグ */
      isLoaded: false,
      // 追加ボタンの押下条件でインターネットゾーンは存在することが確定している
      internetZone: {} as Type1Zone,
      extraZone: {} as Type1Zone | null,
      privateZone: [] as Type1Zone[],
      zoneOption: [] as Type1Zone[],

      form: {
        srcZone: null as null | Type1ZoneOption,
        dstZone: null as null | Type1ZoneOption,
      },
    };
  },
  computed: {
    /** ゾーン一覧（インターネット、エクストラ、プライベート） */
    zoneOptions(): { value: Type1ZoneOption; text: string }[] {
      return [
        {
          value: { ...this.internetZone, type: "INTERNET" },
          text: "グローバルゾーン/" + this.internetZone.zoneName,
        },
        ...(this.extraZone
          ? [
              {
                value: { ...this.extraZone, type: "EXTRA" } as Type1ZoneOption,
                text: "エクストラゾーン/" + this.extraZone.zoneName,
              },
            ]
          : []),
        ...this.privateZone.map((v) => ({
          value: { ...v, type: "PRIVATE" } as Type1ZoneOption,
          text: "プライベートゾーン/" + v.zoneName,
        })),
      ];
    },
    /**
     * 宛先ゾーン一覧.
     * 選択済の送信元ゾーンによって選択不可な選択肢をフィルタリング
     */
    dstZoneOptions(): { value: Type1ZoneOption; text: string }[] {
      if (this.form.srcZone) {
        return this.zoneOptions.filter(({ value }) => {
          if (this.form.srcZone!.type === "INTERNET") {
            return value.type === "EXTRA" || value.type === "PRIVATE";
          } else {
            return value.type === "INTERNET";
          }
        });
      } else {
        return this.zoneOptions;
      }
    },
  },
  watch: {
    /** 送信元ゾーンが選択された際に宛先ゾーンが選択不可な値が入っていたらクリア */
    "form.srcZone"(value: Type1ZoneOption) {
      const dstZoneType = this.form.dstZone?.type;
      if (value.type === "INTERNET") {
        if (dstZoneType === "INTERNET") {
          this.form.dstZone = null;
        }
      } else {
        if (dstZoneType === "EXTRA" || dstZoneType === "PRIVATE") {
          this.form.dstZone = null;
        }
      }
    },
  },
  async mounted() {
    await this.load();
    this.isLoaded = true;
  },
  methods: {
    /** データ取得 */
    async load() {
      this.internetZone = (
        await this.$api.type1SiteZone.getType1InternetList()
      ).zone!;
      this.extraZone = (await this.$api.type1SiteZone.getType1ExtraList()).zone;
      this.privateZone = (
        await this.$api.type1SiteZone.getType1PrivateList()
      ).zoneList.sortBy(["zoneName", "asc"]);
    },
    async submit() {
      await this.$confirm(`ゾーン接続を追加します。よろしいですか？`);
      await this.$api.zoneRelation.postZoneRelation({
        srcZoneId: this.form.srcZone!.zoneId,
        dstZoneId: this.form.dstZone!.zoneId,
        zoneRelationKeyId: this.zoneRelationKeyId,
      });
      (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
    },
  },
});
</script>
